import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setModalOpen } from 'redux/shortcuts/reducer';

interface WithHotkeyProps {
  hotkey: string;
  classes?: string;
  position?: 'top' | 'right' | 'bottom' | 'left' | 'rightInside';
  needShow?: boolean;
  children: React.ReactElement;
}

const positionClasses = {
  top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-1',
  right: 'left-full top-1/2 transform -translate-y-1/2 ml-1',
  bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-1',
  left: 'right-full top-1/2 transform -translate-y-1/2 mr-1',
  rightInside: 'right-0 top-1/2 transform -translate-y-1/2 mr-1',
};

const WithHotkey: React.FC<WithHotkeyProps> = ({
  hotkey,
  classes = '',
  position = 'rightInside',
  needShow = true,
  children,
}) => {
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(false);
  const openModal = () => dispatch(setModalOpen(true));

  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    setIsTouchDevice(
      'ontouchstart' in window || navigator.maxTouchPoints > 0
    );
  }, []);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.altKey && needShow) {
      setIsVisible(true);
    }
  };
  const handleKeyUp = (e: KeyboardEvent) => {
    if (!e.altKey && needShow) {
      setIsVisible(false);
    }
  };

  useEffect(() => {
    isVisible && setIsVisible(false)
  }, [needShow])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleMouseEnter = () => {
    if (needShow && !isTouchDevice) {
      setIsVisible(true);
    }
  };
  const handleMouseLeave = () => {
    if (needShow && !isTouchDevice) {
      setIsVisible(false);
    }
  };

  return (
    <div
      className={`relative ${classes}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {isVisible && (
        <span
          className={`absolute ${positionClasses[position]} z-50 flex items-center gap-x-1 py-px px-1.5 bg-white border border-theme-border rounded-md shadow-xs dark:bg-neutral-700 dark:border-theme-border text-gray-400 dark:text-gray-300`}
        >
          <svg
            onClick={openModal}
            className="shrink-0 size-2.5"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3" />
          </svg>
          <span className="text-[11px] uppercase">{hotkey}</span>
        </span>
      )}
    </div>
  );
};

export default WithHotkey;